
import { Component, Vue, Ref } from 'vue-property-decorator'
import type { ConfirmModalOptions } from '@xt/client/entities/kindred-spirit/kindred-spirit'
import ConfirmModal from '../ConfirmModal/index.vue'
import lodash from 'lodash'
import { Observer } from 'mobx-vue'

export enum CongratulateType {
  Init = 'congratulate-init',
  Recruit = 'congratulate-recruit'
}

export type CongratulateModalData = ConfirmModalOptions & {
  type?: CongratulateType
  bgPath?: string
  cityName?: string
}

// 弹窗消息队列单项类型
interface ModalQueueItem {
  type: CongratulateType
  bgPath: string
  cityName: string
  bizId: string
}

@Observer
@Component({
  components: {
    ConfirmModal
  }
})
export default class CongratulateModalQueue extends Vue {
  defaultOptions: ConfirmModalOptions = {
    confirmedHide: true,
    confirmText: '去浏览',
    cancelText: '关闭'
  }
  currentModalData = {
    title: '',
    subTitle: '',
    description: '',
    type: CongratulateType.Init
  }

  @Ref()
  confirmModal!: {
    show: (options: ConfirmModalOptions) => Promise<boolean>
    hide: () => void
  }

  private modalQueue: ModalQueueItem[] = []
  private isModalShowing = false
  private isDestroyed = false

  get GuildStore() {
    return this.$store.$tonghao.Guild
  }

  get isRecruitModel() {
    return this.currentModalData?.type === CongratulateType.Recruit
  }

  get isInitModel() {
    return this.currentModalData?.type === CongratulateType.Init
  }

  /** 外部调用入口，拉取消息生成弹窗队列 */
  async checkShowCongratulateModal() {
    if (!this.$store.$storeUser.UserInfo.id || this.isDestroyed) return

    if (this.isModalShowing && this.modalQueue.length) return

    const res = await this.$store.$storeUser.messageNotice.onLoadPop()

    if (!Array.isArray(res) || !res.length) return

    res.forEach(item => {
      const parsed = JSON.parse(item.bizData!.jsonContent!) as { regionName: string; guildName: string }
      let queueItem: ModalQueueItem | null = null

      if (item.noticeSubType === 50100) {
        queueItem = {
          type: CongratulateType.Recruit,
          bgPath: 'congratulate-recruit',
          cityName: parsed.regionName,
          bizId: item.bizId
        }
      } else if (item.noticeSubType === 50101) {
        queueItem = {
          type: CongratulateType.Init,
          bgPath: 'congratulate-init',
          cityName: parsed.regionName,
          bizId: item.bizId
        }
      }

      if (queueItem) this.modalQueue.push(queueItem)
    })

    // 开始消费队列
    this.consumeModalQueue()
  }

  private async consumeModalQueue() {
    if (!this.modalQueue.length || this.isModalShowing || this.isDestroyed) return

    this.isModalShowing = true
    const item = this.modalQueue.shift()!

    const rawOpts = lodash.merge({}, this.defaultOptions, {
      type: item.type,
      bgPath: item.bgPath,
      cityName: item.cityName
    } as CongratulateModalData)
    const backgroundUrl = rawOpts.bgPath ? `${this.$store.$global.static}/images/topic/pc/${rawOpts.bgPath}-pop-bg.png` : ''
    const layoutClassName = rawOpts.type ? `${rawOpts.type}-confirm-modal` : 'congratulate-confirm-modal'
    const cleanOpts: ConfirmModalOptions = {
      ...rawOpts,
      layoutClassName,
      backgroundUrl
    }
    delete (cleanOpts as any).type
    delete (cleanOpts as any).bgPath
    delete (cleanOpts as any).cityName

    this.currentModalData = {
      title: `${item.cityName}分桐`,
      subTitle: item.type === CongratulateType.Init ? '成功开启啦~' : '助力成功',
      description: item.type === CongratulateType.Init ? `恭喜${item.cityName}桐好，今后笔墨相伴，常赴雅约` : '分桐负责人招募开启',
      type: item.type
    }

    const confirmResult = await this.confirmModal.show(cleanOpts)

    if (confirmResult === true) {
      window.open(`/topic/city/${item.bizId}`, '_blank')
    }
  }

  hide() {
    this.confirmModal.hide()
  }

  async handleModalClosed() {
    this.isModalShowing = false

    await this.$sleep(200)

    this.consumeModalQueue()
  }

  created(): void {
    this.checkShowCongratulateModal()
  }

  destroyed(): void {
    this.isDestroyed = true
    this.modalQueue = []
    this.isModalShowing = false
    this.hide()
  }
}
